import { motion } from "framer-motion";
import Navigation from "../../components/Navigation";
import PageHero from "../../components/PageHero";

// Animation constants - extracted to prevent inline object recreation
const FADE_IN_UP = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const SLIDE_IN_DELAYS = {
  first: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.1 } },
  second: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.2 } },
  third: { initial: { opacity: 0, x: -20 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.5, delay: 0.3 } }
};

// Font style constant
const PLAYFAIR_STYLE = { fontFamily: "'Playfair Display', serif" };

// Data constants
const MOST_NEEDED_ITEMS = [
  "Vegetable oil (1 or 2 liter bottles)",
  "Spices (black pepper, paprika, dried herbs)",
  "Condiments (ketchup, mustard, mayonnaise, soy sauce)",
  "Rice (1 or 2 lb bags)",
  "Pasta and pasta sauce",
  "Canned corn",
  "Dry beans (1 or 2 lb bags)",
  "Diapers (larger sizes most needed)",
  "Sanitary pads (please no tampons)",
  "Soap, shampoo, conditioner, toothpaste, deodorant, etc.",
];

const OTHER_DONATIONS_GUIDELINES = [
  { id: "clothing", text: "Clothing must be seasonally appropriate and clean, no stains or rips" },
  { id: "housewares", text: "Housewares should have no breaks, rust spots, or other damage" },
  { id: "toys", text: "Toys must be new and unwrapped, accepted for Christmas and Eid only" },
  { id: "school", text: "School supplies must be new or gently used, accepted in August/September and December/January only" }
];

const ItemsNeededPage = () => {
  return (
    <div data-testid="items-needed-page" className="min-h-screen bg-[#FAFAFA]">
      <Navigation />
      
      <PageHero 
        image="https://customer-assets-7cd3h4nn.emergentagent.net/job_4bb091c1-e4be-474e-9067-19b51e555ba6/artifacts/0mdb2rzq_PXL_20260414_141047472~3.webp"
        alt="Items Needed - Murphy's Giving Market"
        objectPosition="top"
      />

      {/* Items Needed Content */}
      <section className="py-12 md:py-20 px-4 md:px-8 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={FADE_IN_UP.initial}
            animate={FADE_IN_UP.animate}
            transition={FADE_IN_UP.transition}
          >
            <h1 
              data-testid="items-needed-title"
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#1A1A1A] mb-8"
              style={PLAYFAIR_STYLE}
            >
              Items Needed
            </h1>
            
            {/* Introduction */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8">
              <p className="text-lg text-[#333333] leading-relaxed">
                Murphy's Giving Market is happy to accept unopened and unexpired shelf-stable food and hygiene items, and clean, new/gently used clothing and household items.
              </p>
            </div>

            {/* Most Needed Items */}
            <motion.div
              initial={SLIDE_IN_DELAYS.first.initial}
              animate={SLIDE_IN_DELAYS.first.animate}
              transition={SLIDE_IN_DELAYS.first.transition}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8"
            >
              <h2 
                className="text-2xl font-bold text-[#C41E3A] mb-6"
                style={PLAYFAIR_STYLE}
              >
                Most Needed Items
              </h2>
              <ul className="space-y-3">
                {MOST_NEEDED_ITEMS.map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <span className="text-[#C41E3A] mt-1.5">•</span>
                    <span className="text-[#333333] text-lg">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Other Donations */}
            <motion.div
              initial={SLIDE_IN_DELAYS.second.initial}
              animate={SLIDE_IN_DELAYS.second.animate}
              transition={SLIDE_IN_DELAYS.second.transition}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 md:p-8 mb-8"
            >
              <h2 
                className="text-2xl font-bold text-[#C41E3A] mb-4"
                style={PLAYFAIR_STYLE}
              >
                Other Donations
              </h2>
              <p className="text-[#333333] text-lg mb-4">
                Depending on the season, we also accept clean, new or gently used clothes, housewares, toys, and school supplies. Please ensure all donations reflect respect for our clients.
              </p>
              <ul className="mt-4 space-y-2">
                {OTHER_DONATIONS_GUIDELINES.map((guideline) => (
                  <li key={guideline.id} className="flex items-start gap-3">
                    <span className="text-[#C41E3A] mt-1.5">•</span>
                    <span className="text-[#333333]">{guideline.text}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Drop-off Hours */}
            <motion.div
              initial={SLIDE_IN_DELAYS.third.initial}
              animate={SLIDE_IN_DELAYS.third.animate}
              transition={SLIDE_IN_DELAYS.third.transition}
              className="bg-[#C41E3A]/10 rounded-xl border border-[#C41E3A]/20 p-6 md:p-8"
            >
              <h2 
                className="text-2xl font-bold text-[#C41E3A] mb-4"
                style={PLAYFAIR_STYLE}
              >
                Donation Drop-off Hours
              </h2>
              <p className="text-[#333333] text-lg">
                <strong>Friday – Tuesday, 9AM – 12PM</strong>
              </p>
              <p className="text-[#333333] mt-2">
                or by appointment
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ItemsNeededPage;
